import envConfig from '@hulu/env-config';
import Cookie from 'universal-cookie';
import { CookieSetOptions } from 'universal-cookie';

import type { DynamicCognitoConfig } from '../../hooks/auth/useCognitoDynamicConfiguration';

const LONG_MAX_AGE = 3 * 86400; // 3 days (Cognito refresh_token lifetime)

const COOKIE_NAMES = [
  'id_token',
  'access_token',
  'refresh_token',
  'device_key',
  'selected_organization_id',
  'user_type',
  'creative_studio_enabled',
] as const;

export type SharedAuthCookies = {
  id_token: string | undefined;
  access_token: string | undefined;
  refresh_token: string | undefined;
  device_key: string | undefined;
  user_type: string | undefined;
  creative_studio_enabled: string | undefined;
};

let adminClientId = envConfig.REACT_APP_COGNITO_ADMIN_CLIENT_ID;
let marketerClientId = envConfig.REACT_APP_COGNITO_MARKETER_CLIENT_ID;
let cognitoRegion = envConfig.REACT_APP_COGNITO_REGION;
let cognitoUserPoolId = envConfig.REACT_APP_COGNITO_USER_POOL_ID;

export function setCognitoVariables(config: DynamicCognitoConfig): void {
  adminClientId = config.adminClientID;
  marketerClientId = config.marketerClientID;
  cognitoRegion = config.region;
  cognitoUserPoolId = config.userPoolID;
}

export function getMarketerCognitoClientId(): string {
  return marketerClientId;
}

export function getCognitoRegion(): string {
  return cognitoRegion;
}

export function getCognitoUserPoolId(): string {
  return cognitoUserPoolId;
}

// Maps the current DCM host to the broadest parent domain shared with the other
// apps that need to read these cookies (Genie, GQL, asset reviewer, help site, etc.).
// Browsers reject Set-Cookie when the Domain attribute is not a suffix of the
// current origin, so this must be derived from window.location.hostname — a
// single env-driven value would only ever work on one of the three apexes we
// serve DCM from.
function getCookieDomain(): string {
  const host = typeof window !== 'undefined' && window.location?.hostname ? window.location.hostname : '';

  if (host === 'localhost') return 'localhost';
  if (host === 'disneyadvertisingqa.com' || host.endsWith('.disneyadvertisingqa.com')) {
    return '.disneyadvertisingqa.com';
  }
  if (host === 'disneyadvertising.com' || host.endsWith('.disneyadvertising.com')) {
    return '.disneyadvertising.com';
  }
  if (host === 'staging.hulu.com' || host.endsWith('.staging.hulu.com')) {
    return '.staging.hulu.com';
  }
  return host || 'localhost';
}

function baseOptions(): CookieSetOptions {
  return {
    domain: getCookieDomain(),
    path: '/',
    secure: true,
    sameSite: 'lax',
  };
}

function persistentOptions(): CookieSetOptions {
  return { ...baseOptions(), maxAge: LONG_MAX_AGE };
}

function getCognitoDeviceKey(userType: 'admin' | 'advertiser'): string | undefined {
  if (typeof window === 'undefined' || !window.localStorage) return undefined;

  const clientId = userType === 'admin' ? adminClientId : marketerClientId;
  if (!clientId) return undefined;

  const lastAuthUser = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`);
  if (!lastAuthUser) return undefined;

  return localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.deviceKey`) ?? undefined;
}

export function restoreCognitoDeviceKeyInLocalStorage(
  userType: 'admin' | 'advertiser',
  username: string,
  deviceKey: string
): void {
  if (typeof window === 'undefined' || !window.localStorage) return;

  const clientId = userType === 'admin' ? adminClientId : marketerClientId;
  if (!clientId || !username || !deviceKey) return;

  const prefix = `CognitoIdentityServiceProvider.${clientId}`;
  localStorage.setItem(`${prefix}.LastAuthUser`, username);
  localStorage.setItem(`${prefix}.${username}.deviceKey`, deviceKey);
}

export function getSharedAuthCookies(): SharedAuthCookies {
  const cookies = new Cookie();

  return {
    id_token: cookies.get('id_token'),
    access_token: cookies.get('access_token'),
    refresh_token: cookies.get('refresh_token'),
    device_key: cookies.get('device_key'),
    user_type: cookies.get('user_type'),
    creative_studio_enabled: cookies.get('creative_studio_enabled'),
  };
}

export function syncSharedAuthCookies(
  tokens: { id_token: string; access_token: string; refresh_token: string },
  userType: 'admin' | 'advertiser',
  orgId?: string
): void {
  const cookies = new Cookie();
  const session = baseOptions();
  const persistent = persistentOptions();

  cookies.set('id_token', tokens.id_token, session);
  cookies.set('access_token', tokens.access_token, session);
  cookies.set('refresh_token', tokens.refresh_token, persistent);
  cookies.set('user_type', userType, persistent);

  const deviceKey = getCognitoDeviceKey(userType);
  if (deviceKey) {
    cookies.set('device_key', deviceKey, persistent);
  } else {
    // The signed-in user has no remembered-device key of their own (never checked
    // "Remember this device", or a different user previously authenticated on this
    // browser) — clear any stale device_key left in the shared cookie so a prior
    // session's remembered-device state doesn't leak into this one (SSAT-19907).
    cookies.remove('device_key', baseOptions());
  }

  if (orgId) {
    cookies.set('selected_organization_id', orgId, persistent);
  }
}

export function getSelectedOrganizationIdCookie(): string | undefined {
  const cookies = new Cookie();
  return cookies.get('selected_organization_id');
}

export function updateSelectedOrganizationCookie(orgId: string): void {
  const cookies = new Cookie();
  cookies.set('selected_organization_id', orgId, persistentOptions());
}

// Removes only the org-context cookie. `selected_organization_id` is persistent (3 days) and
// scoped to the shared parent domain, so it can outlive a session and leak across users on the
// same browser. Clear it on login and when a handoff rejects an inaccessible org so a stale value
// is not forwarded to downstream apps (which would 403).
export function clearSelectedOrganizationCookie(): void {
  const cookies = new Cookie();
  cookies.remove('selected_organization_id', baseOptions());
}

export function updateCreativeStudioEnabledCookie(enabled: boolean): void {
  const cookies = new Cookie();
  cookies.set('creative_studio_enabled', String(enabled), persistentOptions());
}

export function clearSharedAuthCookies(): void {
  clearSharedAuthCookiesForLogout({ preserveDeviceKey: false });
}

export function clearSharedDeviceKeyCookie(): void {
  const cookies = new Cookie();
  cookies.remove('device_key', baseOptions());
}

/** Clears shared auth cookies on logout; preserves device_key when device trust should survive sign-out. */
export function clearSharedAuthCookiesForLogout(options: { preserveDeviceKey: boolean }): void {
  const cookies = new Cookie();
  const cookieOptions = baseOptions();
  const namesToClear = options.preserveDeviceKey ? COOKIE_NAMES.filter((name) => name !== 'device_key') : COOKIE_NAMES;

  namesToClear.forEach((name) => cookies.remove(name, cookieOptions));
}
